import type { HttpClient, RequestOptions } from '../client.js';
import {
  type ProtocolContractsData,
  type ProtocolAddressData,
  type RegistrySlugsData,
  getProtocolsParamsSchema,
  getProtocolParamsSchema,
  getRegistryContractsParamsSchema,
  getRegistryAddressParamsSchema,
  getRegistrySlugsParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type { ProtocolEntry } from '../types/index.js';
import { parseParams } from '../internals/validation.js';

/** Protocol registry — protocols, contracts, chain metadata */
export class RegistryNamespace {
  constructor(private readonly client: HttpClient) {}

  /** List protocols, optionally filtered by category or chain. */
  async getProtocols(
    filter?: { category?: string; chain?: string },
    options?: RequestOptions,
  ): Promise<ProtocolEntry[]> {
    const validated = parseParams(getProtocolsParamsSchema, {
      category: filter?.category,
      chain: filter?.chain,
    });
    return this.client.jsonRpc<ProtocolEntry[]>('getProtocols', validated, options);
  }

  /** Get full details for a protocol by slug. */
  async getProtocol(slug: string, options?: RequestOptions): Promise<ProtocolEntry> {
    const validated = parseParams(getProtocolParamsSchema, { slug });
    return this.client.jsonRpc<ProtocolEntry>('getProtocol', validated, options);
  }

  async getRegistrySlugs(
    filter?: { category?: string; chain?: string },
    options?: RequestOptions,
  ): Promise<RegistrySlugsData> {
    const validated = parseParams(getRegistrySlugsParamsSchema, {
      category: filter?.category,
      chain: filter?.chain,
    });
    return this.client.jsonRpc<RegistrySlugsData>('getRegistrySlugs', validated, options);
  }

  /** Get contract ABIs and addresses for a protocol. */
  async getProtocolContracts(
    slug: string,
    params?: { chain?: string },
    options?: RequestOptions,
  ): Promise<ProtocolContractsData> {
    const validated = parseParams(getRegistryContractsParamsSchema, {
      slug,
      chain: params?.chain,
    });
    return this.client.jsonRpc<ProtocolContractsData>('getRegistryContracts', validated, options);
  }

  /** Resolve a specific contract address for a protocol on a chain. */
  async getAddress(
    slug: string,
    contract: string,
    chain: string,
    options?: RequestOptions,
  ): Promise<ProtocolAddressData> {
    const validated = parseParams(getRegistryAddressParamsSchema, { slug, contract, chain });
    return this.client.jsonRpc<ProtocolAddressData>('getRegistryAddress', validated, options);
  }

  /** Get all protocol categories. */
  async getCategories(options?: RequestOptions): Promise<string[]> {
    return this.client.jsonRpc<string[]>('getRegistryCategories', {}, options);
  }

  /** Get all supported chains. */
  async getChains(options?: RequestOptions): Promise<unknown[]> {
    return this.client.jsonRpc<unknown[]>('getRegistryChains', {}, options);
  }
}
